import {
    Area,
    AreaChart,
    CartesianGrid,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import type { SeriesPoint } from '~/api/types'
import { CHART_UI, VERDICT_COLOR } from '~/features/monitoring/chart-colors'

// 차트 색은 공유 팔레트(chart-colors)에서 가져온다(트렌드·보드 단일 출처).
const C = {
    line: CHART_UI.line,
    grid: CHART_UI.grid,
    axis: CHART_UI.axis,
    threshold: CHART_UI.threshold,
    이탈: VERDICT_COLOR['이탈'],
    근접: VERDICT_COLOR['근접'],
    적합: VERDICT_COLOR['적합'],
}

type Pt = { t: string; full: string; v: number; verdict: string }

/** "2026-04-07T07:41" → "04-07 07:41" (축 라벨용). */
function fmt(iso?: string | null): { t: string; full: string } {
    const s = (iso ?? '').slice(0, 16).replace('T', ' ')
    return { full: s, t: s.slice(5) }
}

function dotColor(verdict: string): string {
    if (verdict === '이탈') return C.이탈
    if (verdict === '근접') return C.근접
    return C.line
}

/** 판정별 색 점 — recharts가 cx/cy/payload를 주입(element 형식). */
function VerdictDot(props: { cx?: number; cy?: number; payload?: Pt }) {
    const { cx, cy, payload } = props
    if (cx == null || cy == null || !payload) return <g />
    return (
        <circle
            cx={cx}
            cy={cy}
            r={3.5}
            fill={dotColor(payload.verdict)}
            stroke='#fff'
            strokeWidth={1}
        />
    )
}

function TipBox(props: {
    active?: boolean
    payload?: Array<{ payload: Pt }>
    unit?: string | null
}) {
    const { active, payload, unit } = props
    if (!active || !payload || payload.length === 0) return null
    const p = payload[0].payload
    return (
        <div className='border-border bg-card rounded-md border px-3 py-2 text-xs shadow-md'>
            <p className='text-muted-foreground'>{p.full}</p>
            <p className='text-foreground mt-0.5 font-semibold'>
                {p.v}
                {unit ? ` ${unit}` : ''}
                <span
                    className='ml-1.5 font-medium'
                    style={{ color: dotColor(p.verdict) }}>
                    {p.verdict}
                </span>
            </p>
        </div>
    )
}

/** 측정값 추이 — 인터랙티브 라인/영역차트(날짜축·툴팁·기준선·판정별 점). */
export function TrendChart({
    series,
    threshold,
    unit,
}: {
    series: SeriesPoint[]
    threshold: number | null
    unit?: string | null
}) {
    // 백엔드는 최신순(DESC) → 차트는 오래된→최신(좌→우)로 뒤집는다.
    const data: Pt[] = [...series]
        .reverse()
        .map((s) => {
            const { t, full } = fmt(s.measuredAt)
            return {
                t,
                full,
                v: Number((s.value ?? '').replace(/,/g, '')),
                verdict: s.verdict,
            }
        })
        .filter((d) => Number.isFinite(d.v))

    if (data.length === 0) {
        return (
            <p className='text-muted-foreground text-xs'>
                수치 추이를 그릴 데이터가 없어요.
            </p>
        )
    }

    const vals = data.map((d) => d.v)
    const lo = Math.min(...vals, threshold ?? Infinity)
    const hi = Math.max(...vals, threshold ?? -Infinity)
    const pad = (hi - lo) * 0.15 || Math.abs(hi) * 0.15 || 1

    return (
        <div className='h-72 w-full'>
            <ResponsiveContainer width='100%' height='100%'>
                <AreaChart
                    data={data}
                    margin={{ top: 10, right: 56, left: 0, bottom: 0 }}>
                    <defs>
                        <linearGradient id='mg' x1='0' y1='0' x2='0' y2='1'>
                            <stop
                                offset='0%'
                                stopColor={C.line}
                                stopOpacity={0.18}
                            />
                            <stop
                                offset='100%'
                                stopColor={C.line}
                                stopOpacity={0}
                            />
                        </linearGradient>
                    </defs>
                    <CartesianGrid
                        strokeDasharray='3 3'
                        stroke={C.grid}
                        vertical={false}
                    />
                    <XAxis
                        dataKey='t'
                        tick={{ fontSize: 11, fill: C.axis }}
                        tickLine={false}
                        axisLine={{ stroke: C.grid }}
                        minTickGap={24}
                    />
                    <YAxis
                        tick={{ fontSize: 11, fill: C.axis }}
                        tickLine={false}
                        axisLine={false}
                        width={40}
                        domain={[Math.floor(lo - pad), Math.ceil(hi + pad)]}
                    />
                    <Tooltip
                        content={<TipBox unit={unit} />}
                        cursor={{ stroke: C.axis, strokeDasharray: '3 3' }}
                    />
                    {threshold != null ? (
                        <ReferenceLine
                            y={threshold}
                            stroke={C.threshold}
                            strokeDasharray='6 4'
                            strokeWidth={1.5}
                            label={{
                                value: `기준 ${threshold}`,
                                position: 'right',
                                fill: C.threshold,
                                fontSize: 11,
                            }}
                        />
                    ) : null}
                    <Area
                        type='monotone'
                        dataKey='v'
                        stroke={C.line}
                        strokeWidth={2}
                        fill='url(#mg)'
                        dot={<VerdictDot />}
                        activeDot={{ r: 5, strokeWidth: 0 }}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    )
}
